import { ReactComponent as ChatIcon } from '@ubeya/shared-web/assets/chat.svg';
import { ReactComponent as RecentActionsIcon } from '@ubeya/shared-web/assets/recent-actions.svg';
import { ReactComponent as RequestIcon } from '@ubeya/shared-web/assets/request.svg';
import { ReactComponent as HelpDeskIcon } from '@ubeya/shared-web/assets/module-help-desk.svg';
import { CircleIcon, FlexCenter, FlexMiddle } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useChatIframe from '@ubeya/shared-web/hooks/useChatIframe';
import { auditLogSidebarRef, chatCount, requestsModal, helpDeskSidePanel } from '@ubeya/shared/atoms/shared';
import useRequestsStats from '@ubeya/shared/hooks/account/useRequestsStats';
import useHelpDeskUnreadCount from '@ubeya/shared/hooks/account/useHelpDeskUnreadCount';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useModules from '@ubeya/shared/hooks/account/useModules';
import React, { useCallback, useContext } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import styled from 'styled-components';
import withPrivileges from '@ubeya/shared-web/hoc/withPrivileges';
import useHelpDeskSocket from '../../../helpDesk/useHelpDeskSocket';
import { useHelpDeskAnalytics } from '../../../helpDesk/helpDeskAnalytics';
import RequestsModal from '../RequestsModal';
import AuditLogSidebar from './AuditLogSidebar';

const IconsContainer = styled(FlexMiddle)``;

const MobileChatIcon = styled(ChatIcon)`
  path:first-child {
    stroke: white;
  }
  path:nth-child(2),
  path:nth-child(3) {
    fill: white;
  }
`;

const withWhiteFill = (Component) => styled(Component)`
  fill: white;
`;

const MobileRecentActionsIcon = withWhiteFill(RecentActionsIcon);
const MobileRequestIcon = withWhiteFill(RequestIcon);

const CircleWrapper = styled.div`
  margin-left: 16px;
  position: relative;

  .circle-icon {
    padding: 0;
  }
`;

const Counter = styled(FlexCenter)`
  background: ${({ theme }) => theme.colors.notification};
  border-radius: ${({ $counter }) => ($counter > 9 ? '20px' : '100%')};
  position: absolute;
  min-width: 14px;
  padding: 0 4px;
  height: 15px;
  bottom: -1px;
  right: -2px;
`;

const CounterText = styled.div`
  color: ${({ theme }) => theme.colors.white};
  font-size: 10px;
  line-height: 15px;
  text-align: center;
`;

const CircleButton = withPrivileges(({ icon: Icon, title, counter, onClick, isMobile, id }) => {
  return (
    <CircleWrapper onClick={onClick} id={id}>
      <CircleIcon backgroundColor={isMobile && 'primary'} icon={Icon} tooltip={title} />
      {counter > 0 && (
        <Counter $counter={counter}>
          <CounterText>{counter >= 100 ? '+99' : counter}</CounterText>
        </Counter>
      )}
    </CircleWrapper>
  );
});

export const AccountHeaderIcons = ({ isMobile }) => {
  const { t } = useContext(ConfigContext);
  const [isRequestModalOpen, setIsRequestsModalOpen] = useRecoilState(requestsModal);
  const [auditLogSidebarPayload, setAuditLogSidebarPayload] = useRecoilState(auditLogSidebarRef);
  const [helpDeskPanel, setHelpDeskPanel] = useRecoilState(helpDeskSidePanel);
  const { accountId } = useAccount();
  const { modules } = useModules();
  const { stats, isLoading: isLoadingStats } = useRequestsStats();
  const { isOpen, handleToggle, hasViewChatPrivilege } = useChatIframe();
  const chatCounter = useRecoilValue(chatCount);
  const { count: helpDeskUnreadCount } = useHelpDeskUnreadCount({ accountId });
  const { trackHelpDeskEvent } = useHelpDeskAnalytics();
  useHelpDeskSocket({ accountId, enabled: !!modules?.help_desk });

  const setIsAuditLogSideBarOpen = useCallback(() => setAuditLogSidebarPayload({ isOpen: true }), [
    setAuditLogSidebarPayload
  ]);

  const toggleHelpDeskPanel = useCallback(() => {
    const nextIsOpen = !helpDeskPanel?.isOpen;

    if (nextIsOpen) {
      trackHelpDeskEvent({
        eventName: 'Help Desk - Admin Opened Module',
        context: 'navigated to Help Desk from top bar'
      });
    }

    setHelpDeskPanel({ isOpen: nextIsOpen, ticketId: null });
  }, [helpDeskPanel?.isOpen, setHelpDeskPanel, trackHelpDeskEvent]);

  return (
    <>
      <IconsContainer>
        <CircleButton
          isMobile={isMobile}
          icon={isMobile ? MobileRecentActionsIcon : RecentActionsIcon}
          title={t('auditLogs')}
          onClick={() => setIsAuditLogSideBarOpen()}
        />
        <CircleButton
          isMobile={isMobile}
          icon={isMobile ? MobileRequestIcon : RequestIcon}
          counter={isLoadingStats ? 0 : stats.numOfPending}
          title={t('requests')}
          onClick={() => setIsRequestsModalOpen(true)}
        />
        {hasViewChatPrivilege && (
          <CircleButton
            isMobile={isMobile}
            id={isMobile ? 'chat-button-mobile' : 'chat-button'}
            icon={isMobile ? MobileChatIcon : ChatIcon}
            counter={chatCounter?.payload}
            title={t('chat')}
            onClick={() => handleToggle(!isOpen)}
          />
        )}
        {modules?.help_desk && (
          <CircleButton
            isMobile={isMobile}
            icon={HelpDeskIcon}
            counter={helpDeskUnreadCount}
            title={t('help_desk')}
            onClick={toggleHelpDeskPanel}
          />
        )}
      </IconsContainer>
      {auditLogSidebarPayload.isOpen && (
        <AuditLogSidebar
          showExport={auditLogSidebarPayload?.showExport}
          onClose={() => {
            setAuditLogSidebarPayload({ isOpen: false });
          }}
        />
      )}
      {isRequestModalOpen ? <RequestsModal onClose={() => setIsRequestsModalOpen(false)} /> : null}
    </>
  );
};
